/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly, CheckBoxComp } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, ApplicationDTO, ApplicationFilterDTO, CompanyDTO, CompanyFilterDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface IApplicationMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const ApplicationMaintenance: React.FC<IApplicationMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [applicationDTO, setApplicationDTO] = useState<ApplicationDTO>();
    const [applicationFilterDTO, setApplicationFilterDTO] = useState<ApplicationFilterDTO>();
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setApplicationDTO({
            ...applicationDTO,
            id: '',
            name: '',
            description: '',
            url: '',
            statusId: '',
            typeId: '',
            isLocal: true,
            companyId: ''
        } as ApplicationDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.appsphere.application.typeId, setTypeList);
            handleCompanyGetAll();
            handleUserGetAllAsync();
            handleGetApplicationByIdAsync();

            handleGetPermission(ListId.appsphere.applicationId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList,);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async () => {
        const userFilterDTO = {
            applicationId: userInfo.user.applicationId,
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetApplicationByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('appsphere.application', data.id, setLoading, setApplicationDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = applicationDTO as ApplicationDTO;

        if (applicationDTO?.id === '' || applicationDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('appsphere.application', dto, setApplicationDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if(!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('appsphere.application', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [applicationDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='APPPLICATION DETAILS'
                icon='grid_view_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={applicationDTO?.name}
                                setValue={setApplicationDTO}
                                obj={applicationDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='typeId'
                                label='Type'
                                placeholder='Type'
                                value={applicationDTO?.typeId}
                                setValue={setApplicationDTO}
                                obj={applicationDTO}
                                valueList={typeList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={applicationDTO?.description}
                                setValue={setApplicationDTO}
                                obj={applicationDTO}
                                required={false}
                                multiline={true}
                                rows={4}
                                maxLength={400}
                                autoFocus={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='url'
                                label='URL'
                                value={applicationDTO?.url}
                                setValue={setApplicationDTO}
                                obj={applicationDTO}
                                maxLength={400}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <SelectField
                                id='companyId'
                                label='Company'
                                placeholder='Status'
                                value={applicationDTO?.companyId}
                                setValue={setApplicationDTO}
                                obj={applicationDTO}
                                valueList={companyList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid container item xs={12} md={12} sx={{ justifyContent: 'end' }}>
                            <CheckBoxComp
                                id='isLocal'
                                label='Local Application'
                                setValue={setApplicationFilterDTO}
                                objDTO={applicationFilterDTO}
                                defaultChecked={applicationFilterDTO?.isLocal}
                                disabled={false}
                            />
                        </Grid>
                        {applicationDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={applicationDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === applicationDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(applicationDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === applicationDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(applicationDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};
